import { GetProjectPublishedStatus } from '@/connection/projects/get-project-published-status';
import { RootState } from '@/store/main';
import { serializeError, getPayloadMessage, SerializedError } from '@/store/utils/serialize-error';
import toastTrigger from '@ui/toast/toast-trigger';
import loadWorkingProjectWithIndexedDBReducerHelper from '../helpers/load-working-project-with-indexed-db-reducer-helper';
import publishWorkingProjectReducerHelper from '../helpers/publish-working-project-reducer-helper';
import { ProjectStateReducerCreator } from '../types';

const publishWorkingProjectReducer = (create: ProjectStateReducerCreator) => {
  return create.asyncThunk(
    async ({ publishedUrl }: { publishedUrl?: string }, { rejectWithValue, getState, dispatch }) => {
      try {
        const rootState = getState() as RootState;

        if (rootState.project.savingWorkingProject) {
          return rejectWithValue(
            'Publish attempted during project save. Please wait a few seconds until the save is complete and try again.'
          );
        }

        const workingProjectUuid = (getState() as RootState).project?.workingProject?.uuid;
        if (!workingProjectUuid) {
          return rejectWithValue('No working project found to publish');
        }
        await publishWorkingProjectReducerHelper({ dispatch, getState, publishedUrl });

        const refetchedProject = await loadWorkingProjectWithIndexedDBReducerHelper({
          project: null,
          projectUuid: workingProjectUuid,
          resetValues: false,
          withCache: false,
          getState,
        });

        let publishedStatus = rootState.project.projectPublishedStatus as { status: number; url: string } | null;
        if (publishedUrl) {
          // refetch the published status to update the URL
          const refetchedPublishedStatus = await GetProjectPublishedStatus(workingProjectUuid);
          if ('error' in refetchedPublishedStatus) {
            console.error('getPublishedEventUrl error', refetchedPublishedStatus.error);
          } else {
            publishedStatus = refetchedPublishedStatus;
          }
        }

        return { project: refetchedProject, publishedStatus };
      } catch (e) {
        console.error(e);
        return rejectWithValue(serializeError(e));
      }
    },
    {
      fulfilled: (state, action) => {
        state.publishingProject = false;
        if (action.payload?.project) {
          state.workingProject = action.payload.project;
        }
        if (action.payload?.publishedStatus) {
          state.projectPublishedStatus = action.payload.publishedStatus;
        }
      },
      pending: (state) => {
        state.publishingProject = true;
      },
      rejected: (state, action) => {
        state.publishingProject = false;
        console.error(action);
        const msg =
          getPayloadMessage(action.payload as SerializedError | string | undefined) ||
          action.error?.message ||
          'An error occurred while publishing the project.';
        toastTrigger(
          `<div>
              <p class="S1">${msg}</p>
            </div>`,
          {
            type: 'error',
            autoClose: 10000,
            closeOnClick: true,
          }
        );
      },
    }
  );
};

export default publishWorkingProjectReducer;
